import ExcelJS from "exceljs";
import { getNgieRowConfig } from "../../../pages/group-data/utils/ngieUtils";
import { getSelectedPeriod } from "../../../services/GroupContext";
import {
  addColumnValues,
  addGroupDataFileProtection,
  addGroupDataTableStyles,
  addInstructionsWorksheet,
  addNoteToCell,
  addValidations,
  hideColumn,
  saveExcelFile,
} from "./DataImportUtils";
import { handleAngieDownloadTemplate } from "./angieDataImportUtil";
import { handleEbitdaTemplateDownload } from "./ebitdaDataImportUtil";
import { handleNgieTemplateDownload } from "./ngieDataImportUtil";
import { handleQngieDownloadTemplate } from "./qngieDataImportUtil";

const instructionsSheetBuilder = [
  {
    value: "User Information and Guidance - Group Data Import",
    type: "header",
  },
  { value: "" },
  { value: "Uploading / Downloading", type: "header" },
  {
    value:
      'This template allows the user to enter the details of the Net Group-Interest Expense ("NGIE"), Adjusted Net Group-Interest Expense ("ANGIE"), Qualifying Net Group-Interest Expense ("QNGIE") and/or Group-EBITDA of the Worldwide Group. It is designed so that the user can add the consolidated trial balance of the group as an additional tab in the spreadsheet and link the numbers through directly, so that the source of the numbers can be traced.',
  },
  {
    value:
      "Depending on the download chosen, either the template consists of a single tab corresponding to the relevant Group Data element, or multiple tabs corresponding to all elements. The group sheets are not linked, so entering NGIE does not change the value of NGIE in the ANGIE sheet until it has been uploaded and re-downloaded.",
  },
  {
    value:
      "Where data has already been entered in the CIR Analyser, this will be downloaded and pre-populated in the template. This data can be edited and, when uploaded, will replace the data currently reflected in the CIR Analyser.",
  },
  {
    value:
      "Once the template is complete and has been saved, return to the relevant pop-up on the Group Data page in the CIR Analyser, select Import/Export Template, and attach the template file before pressing Import Data. The contents of the relevant worksheet (tab) should be uploaded to the relevant pop-up page and replace any existing data that was previously included.",
  },
  {
    value:
      "Uploaded data will not be saved to the CIR Analyser until the Save, Submit, or Next button has been pressed",
  },
  {
    value:
      "If, after uploading the Group Data, if the relevant modal is exited without saving, the uploaded data will not be saved, and the pre-existing data will remain.",
  },
  { value: "" },
  { value: "Entering Data and Inserting New Rows", type: "header" },
  { value: "General", styles: ["underline", "italic"] },
  {
    value:
      'It is possible to complete the data entry using formulas linking to cells in other tabs added to this spreadsheet, or in other spreadsheets. The "NGIE" tab should remain the first tab of the spreadsheet.',
  },
  {
    value:
      "Grey cells are system generated and cannot be edited. White cells can be edited.",
  },
  {
    value:
      "Any number of new rows can be inserted in each section in the same way that rows are normally inserted in Excel.",
  },
  {
    value:
      "Notes have been added to each category to help guide completion of the template - hover over any cell with a red triangle in the top right corner in order to view the notes.",
  },
  {
    value:
      "The preparer and reviewer columns are for internal reporting purposes only and are not uploaded to the CIR Analyser.",
  },
  { value: "" },
  { value: "Currency", styles: ["underline", "italic"] },
  {
    value:
      "Data should be entered in the currency used for the consolidated financial statements.",
  },
  {
    value:
      "The currency and FX rate must be entered in the NGIE pop-up in the CIR Analyser. It is recommended that this is entered before downloading the templates, so that the correct FX rate is stored in the templates.",
  },
  { value: "" },
  { value: "Data Inputs", styles: ["underline", "italic"] },
  {
    value:
      "The description of the amount included should be included in Column A",
  },
  {
    value:
      "The value of the amount included should be included in Column B as a number.",
  },
  {
    value:
      "Where relevant, Column B also reflects the sign convention (i.e. positive or negative) that should be applied to entries in that category. Data valiation ensures that figures cannot be entered with the wrong signage.",
  },
  {
    value:
      "Preparer comments can be included in Column E if desired. These will not be uploaded into the CIR Analyser, but can be used to document any comments or questions of the preparer.",
  },
  {
    value:
      "Review comments can be included in Column F if desired. These will not be uploaded into the CIR Analyser, but can be used to document any comments or questions of the reviewer.",
  },
  { value: "" },
  { value: "Data Validations", type: "header" },
  { value: "The following data validations exist within the spreadsheet:" },
  {
    value:
      'Where Column B denotes the sign convention (i.e. "positive values" or "negative values") that should be applied to entries in that category. Data valiation ensures that figures cannot be entered with the wrong signage.',
  },
  { value: "" },
  { value: "Upload Warnings & Errors", type: "header" },
  {
    value:
      "On upload, the CIR Analyser may identify errors. Errors must be rectified within the spreadsheet and the spreadsheet must be re-uploaded.",
  },
  {
    value: "Errors (prevent uploading - must be corrected and re-uploaded):",
    styles: ["underline", "italic"],
  },
  {
    value:
      'The CIR Analyser cross-checks the structure of the spreadsheet uploaded to ensure that it has all of the categories in the latest download template. Where the upload template does not have one of these (as an earlier version of the template is used or the relevant category has been deleted), an error will arise. The error message will state: "[TBC]".',
  },
  {
    value:
      'The CIR Analyser double checks the sign convention in Column B subject to the data validation above. Where the incorrect signage is used, an error will arise. The error message will state: "[TBC]".',
  },
];

export const getGroupDataInitialSheetBuilder = () => {
  const currency = getSelectedPeriod()?.currency || "GBP";
  const fxRate = getSelectedPeriod()?.fx_rate || "1";
  return [
    {
      label: "Currency",
      type: "subheader",
      value: currency,
      notes: [
        `The currency and fx rate which will be applied to these inputs are: 1 GBP = ${fxRate}${currency}. Any changes to this need to be made in the NGIE section`,
      ],
    },
    {
      label: "FX rate",
      type: "subheader",
      value: fxRate,
      validation: "fourDecimal",
    },
    {
      label: "Overall Total (Before floor of zero) - Local currency:",
      type: "subheader",
      formula: `=TEXT(SUM(B10:B1048576), "#,##0.00")`,
    },
    {
      label: "Overall Total (Minimum of zero) - Local currency:",
      type: "subheader",
      formula: `=TEXT(MAX(VALUE(B4), 0), "#,##0.00")`,
    },
    { label: "", type: "subheader" },
    {
      label: "Overall Total (Before floor of zero) - GBP:",
      type: "subheader",
      formula: `=TEXT(ROUND(VALUE(B4)/VALUE(B3), 0), "#,##0.00")`,
    },
    {
      label: "Overall Total (Minimum of zero) - GBP:",
      type: "subheader",
      formula: `=TEXT(MAX(VALUE(B7), 0), "#,##0.00")`,
    },
    { label: "", type: "subheader" },
  ];
};

export function addGroupDataWorksheet({
  sheetBuilder,
  worksheetName,
  workbook,
  documentName,
  isImportTemplate = true,
  isClientFacing = true,
}) {
  const worksheet = workbook?.addWorksheet(worksheetName);

  const columnsConfig = [
    { name: "A", label: "", type: "text" },
    { name: "B", label: "Value", type: "number" },
    { name: "C", label: "rowType", type: "text" },
  ];

  // add Column A values (headers & subheader labels)
  const labels = sheetBuilder.map((row) => row.label);
  addColumnValues({
    worksheet,
    worksheetName,
    column: "A",
    values: labels,
  });

  // Add Column B values (including Totals)
  const filterByCondition = (sheetBuilder, condition, startIndex) => {
    return sheetBuilder.slice(startIndex + 1).findIndex(condition);
  };

  const getNextIndex = (foundIndex, baseIndex) => {
    return foundIndex !== -1 ? foundIndex + baseIndex + 2 : "1048576";
  };

  const getHeaderSumFormula = (startIndex, endIndex) => ({
    formula: `="Total: " & TEXT(SUM(B${startIndex + 3}:B${endIndex}), "#,##0.00")`,
  });

  const values = sheetBuilder.map((row, index) => {
    if (row.formula) {
      return { formula: row.formula };
    }

    const isUnnamedHeader =
      row.type === "header" &&
      !row.hasOwnProperty("value") &&
      !row.hasOwnProperty("headerType");
    const isTypedHeader = row.hasOwnProperty("headerType");

    if (isUnnamedHeader || isTypedHeader) {
      const condition = isTypedHeader
        ? (r) => r.hasOwnProperty("headerType")
        : (r) => r.type === "header";

      const nextHeaderIndex = filterByCondition(sheetBuilder, condition, index);
      const nextIndex = getNextIndex(nextHeaderIndex, index);
      return getHeaderSumFormula(index, nextIndex);
    }

    return row.value ?? "";
  });
  addColumnValues({ worksheet, column: "B", values });

  // add Column C values (metric keys)
  const keys = sheetBuilder.map((row) =>
    row.type === "header" ? "header" : (row.key ?? ""),
  );
  addColumnValues({ worksheet, column: "C", values: keys });
  hideColumn(worksheet, "C");

  const includeReviewColumns = isImportTemplate && !isClientFacing;

  if (isImportTemplate) {
    // add notes to Column A (headers and subheaders)
    const notes = sheetBuilder.map((row) => row.notes);
    notes.forEach((notesList, index) => {
      if (notesList)
        addNoteToCell({ worksheet, notesList, cellAddress: `A${index + 2}` });
    });

    if (includeReviewColumns) {
      const colDValues = [
        "Preparer Name",
        "",
        "Preparation Complete?",
        "",
        "Completion Date",
        "",
        "",
        "Preparer Comments",
      ];
      addColumnValues({ worksheet, column: "D", values: colDValues });

      const colEValues = [
        "Reviewer Name",
        "",
        "Review Complete?",
        "",
        "Completion Date",
        "",
        "",
        "Reviewer Comments",
      ];
      addColumnValues({ worksheet, column: "E", values: colEValues });

      const colFValues = [
        "Second Preparer Name",
        "",
        "Second Preparation Complete?",
        "",
        "Second Completion Date",
        "",
        "",
        "Second Preparer Comments",
      ];
      addColumnValues({ worksheet, column: "F", values: colFValues });

      const colGValues = [
        "Second Reviewer Name",
        "",
        "Second Review Complete?",
        "",
        "Second Completion Date",
        "",
        "",
        "Second Reviewer Comments",
      ];
      addColumnValues({ worksheet, column: "G", values: colGValues });
    }
  }

  const isNgie = documentName === "NGIE Import Template";
  addGroupDataTableStyles(
    worksheet,
    sheetBuilder,
    isNgie,
    isImportTemplate,
    includeReviewColumns,
  );
  addValidations({ worksheet, columnsConfig, sheetBuilder });

  if (isImportTemplate) {
    addGroupDataFileProtection(worksheet, sheetBuilder, includeReviewColumns);
  }
}

export async function downloadGroupDataImportTemplate({
  sheetBuilder,
  documentName,
  worksheetName,
  isClientFacing = true,
}) {
  const workbook = new ExcelJS.Workbook();

  addGroupDataWorksheet({
    sheetBuilder,
    worksheetName,
    workbook,
    documentName,
    isClientFacing,
  });

  // add Instructions worksheet if not already added:
  addInstructionsWorksheet({
    workbook,
    sheetBuilder: instructionsSheetBuilder,
  });

  await saveExcelFile(workbook, documentName);
}

export async function downloadAllGroupDataImportTemplates({
  existingWorkbook,
  isImportTemplate = true,
  isClientFacing = true,
} = {}) {
  const workbook = existingWorkbook ?? new ExcelJS.Workbook();

  handleNgieTemplateDownload({ workbook, isImportTemplate, isClientFacing });

  handleAngieDownloadTemplate({
    workbook,
    isImportTemplate,
    isClientFacing,
  });

  handleQngieDownloadTemplate({
    workbook,
    isImportTemplate,
    isClientFacing,
  });

  handleEbitdaTemplateDownload({
    workbook,
    isImportTemplate,
    isClientFacing,
  });

  // if condition will be true if downloading import template, but false if adding the previous worksheets to the Outputs page excel file
  if (!existingWorkbook) {
    // add Instructions worksheet if not already added:
    addInstructionsWorksheet({
      workbook,
      sheetBuilder: instructionsSheetBuilder,
    });

    await saveExcelFile(workbook, "Group Data Import Templates");
  }
}

export function validateImportedGroupData({
  importedData,
  sheetBuilder,
  setErrors,
}) {
  const validationKeys = sheetBuilder.filter((row) => row.key);

  // check that all keys exist
  const errors = validationKeys.filter((row) => !(row.key in importedData));

  if (errors.length > 0) {
    setErrors([
      {
        title:
          "There has been an error with the import file, please use the template downloadable from this page without modifications.",
      },
      {
        title: "Imported file is missing rows for each of the following:",
        messages: errors.map((row) => row.label ?? ""),
      },
    ]);

    throw new Error(
      "missing rows while importing Group Data",
      errors.map((row) => row.key ?? "key"),
    );
  }

  const ngieRowConfig = getNgieRowConfig();
  // validate negative / positive values
  const hasInvalidData = Object.entries(importedData).some(([key, rows]) => {
    const { type, label } = ngieRowConfig[key] || {};

    const isInvalid = rows.some((row) => {
      const value = row.value;
      if (value === "") return false;
      const num = Number(value);
      return type === "positive"
        ? num < 0
        : type === "negative"
          ? num > 0
          : false;
    });

    if (isInvalid) {
      setErrors((prev) => [
        ...(prev ?? []),
        { title: `${label} must have ${type} values` },
      ]);
    }

    return isInvalid;
  });

  if (hasInvalidData) {
    throw new Error("Missing rows while importing Group Data");
  }
}

export function handleGroupDataConfirmation({ newData }) {
  return newData;
}
